import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
    Box, Card, CardMedia
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import coverImage from '../../graphics/receipts_dialog_cover.png';
import receiptBoxCategory from '../../graphics/receiptBoxCategory.png';
import receiptBoxPrivate from '../../graphics/receiptBoxPrivate.png';

// Reusable Step Number Circle Component
const StepNumberCircle = ({ step, color }) => (
    <Box
        sx={{
            position: 'absolute',
            top: '-15px', // Half of the circle's height to center it on the top edge
            left: '50%',
            transform: 'translateX(-50%)',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundColor: color, // Changed this line to use 'color' for background
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
            zIndex: 1, // Ensure it's above the card
            border: `2px solid ${color}`, // Border matching the icon color
        }}
    >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}> {/* Changed text color to white for contrast */}
            {step}
        </Typography>
    </Box>
);

function InfoReceipts({ isOpen, onClose }) {

// Common Card Styling
    const commonCardSx = {
        borderRadius: '1rem',
        padding: '1.5rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'transform 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        '&:hover': {
            transform: 'translateY(-5px)',
        },
    };

    // Common Icon Container Styling
    const commonIconContainerSx = {
        padding: '0.75rem',
        borderRadius: '0.5rem',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    // Responsive Box item styling
    const responsiveBoxItemSx = {
        flexGrow: 1, // Allows items to grow
        flexShrink: 1, // Allows items to shrink
        minWidth: '250px', // Minimum width before wrapping
        maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', lg: 'calc(33.33% - 16px)' }, // Responsive max width
        // For lg:col-span-3 (full width on lg screens)
        '&.full-width-lg': {
            maxWidth: { lg: '100%' },
        },
        // For lg:col-span-8 (2/3 width on lg screens)
        '&.two-thirds-width-lg': {
            maxWidth: { lg: 'calc(66.66% - 16px)' },
        },
    };

    // Styling for the Card component itself when it's a step card
    const stepCardBaseSx = {
        ...commonCardSx, // Inherit base card styles
        borderTop: '2px solid', // Add a top border
        paddingTop: '2.5rem', // Increase padding-top to make space for the circle content
        // Remove individual card hover transform here, as it will be on the parent Box
        '&:hover': {
            boxShadow: '0 8px 15px rgba(0, 0, 0, 0.1)', // Keep shadow change
        },
    };

    // Styling for the Box that wraps both the StepNumberCircle and the Card for numbered steps
    const responsiveBoxItemWithStepSx = {
        ...responsiveBoxItemSx, // Inherit base responsive styles
        position: 'relative', // Crucial for positioning the step number circle
        marginTop: '20px', // Push the entire box down to allow space for the circle above the card's border
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out', // Add transition to the parent box
        '&:hover': {
            transform: 'translateY(-5px)', // Apply hover transform to the parent box
        },
    };




    return (
        <Dialog open={isOpen} onClose={onClose}>
            {/* Dialog Title */}
            <DialogTitle
                sx={{
                    color: '#151269',
                    textAlign: 'center',
                    backgroundColor: 'rgba(198, 226, 255, 0.8)',
                    paddingRight: '30px'
                }}
            >
                Expense Tracker: Managing Your Receipts! 💰
            </DialogTitle>

            <Typography
                sx={{
                    fontSize: '14px',
                    textAlign: 'center',
                    color: '#151269',
                    backgroundColor: 'rgba(198, 226, 255, 0.8)',
                    padding: '0 20px'
                }}
            >
                Keep your finances in check and your budget balanced!
                This guide helps you easily track and categorize all your event-related expenses.
            </Typography>

            {/* Close Button */}
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: '#151269',
                }}
            >
                <i className="icon-cross" style={{ fontSize: '18px', padding: '5px' }} />
            </IconButton>

            {/* Dialog Content */}
            <DialogContent sx={{backgroundColor: '#f8fafc' }}>

                {/* Cover Image */}
                {/*
                <Box sx={{ padding: '0 8px 20px 8px'}}>
                    <CardMedia
                        component="img"
                        image={coverImage}
                        alt="Happy Expense Receipts Management"
                        sx={{
                            height: '180px', // Limit height for better layout
                            objectFit: 'cover', // Cover the area, cropping if necessary
                            borderRadius: '12px', // Rounded corners for the image
                            boxShadow: '0 4px 10px rgba(0,0,0,0.1)', // Subtle shadow
                        }}
                    />
                </Box>
                */}


                {/* Ways to Add Receipts Section */}
                <Typography variant="h4" component="h2" sx={{
                    fontSize: '18px',
                    fontWeight: 700, // font-bold
                    color: '#1f2937', // text-gray-800
                    marginBottom: '1.5rem', // mb-6
                    textAlign: 'center',
                }}>
                    Adding New Receipts: Keep Track of Every Penny! 🧾
                </Typography>
                <Typography variant="h6" sx={{
                    fontSize: '14px',
                    color: '#4b5563', // text-gray-600
                    marginBottom: '2rem', // mb-8
                    textAlign: 'center',
                }}>
                    Easily log your expenses with these simple steps:
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '24px', // Equivalent to spacing={3}
                        marginBottom: '3rem',
                        justifyContent: 'center' // Center items when they wrap
                    }}
                >


                    {/* Add the CATEGORY Box item */}
                    <Box sx={{ ...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <Card sx={{ ...commonCardSx, height:'550px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <CardMedia
                                component="img"
                                image={receiptBoxCategory}
                                alt="screenshot for category"
                                sx={{
                                    objectFit: 'cover', // Cover the area, cropping if necessary
                                    borderRadius: '12px', // Rounded corners for the image
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)', // Subtle shadow
                                }}
                            />
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                }}
                            >
                                Category
                            </Typography>
                            <Typography variant="body1" sx={{fontSize: '14px', color: '#4b5563'}}>
                                Categorize your expenses to easily track spending patterns and organize
                                your financial records. Selecting the correct category ensures accurate
                                budgeting and reporting.
                                <br/><br/>
                                <Typography component="span" variant="body2"
                                            sx={{fontWeight: 'bold', color: '#0ea5e9'}}>Pro Tip:</Typography> Receipts
                                linked to Activities, Lodging, or Meals automatically populate
                                the location, cost, and category!
                            </Typography>
                        </Card>
                    </Box>

                    {/* Add the PUBLIC/PRIVATE Box item */}
                    <Box sx={{ ...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <Card sx={{ ...commonCardSx, height:'550px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <CardMedia
                                component="img"
                                image={receiptBoxPrivate}
                                alt="screenshot for private"
                                sx={{
                                    objectFit: 'cover', // Cover the area, cropping if necessary
                                    borderRadius: '12px', // Rounded corners for the image
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)', // Subtle shadow
                                }}
                            />
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                }}
                            >
                                Private
                            </Typography>
                            <Typography variant="body1" sx={{fontSize: '14px', color: '#4b5563'}}>
                                This toggle's primary function is to control the visibility of your receipt.
                                When set to Private, the receipt details are only visible to you.
                                When set to Public, they can be viewed by other attendees.
                                It's designed to give you control over your financial privacy.
                            </Typography>
                        </Card>
                    </Box>


                    {/* Add the LOCATION Box item */}
                    <Box sx={{ ...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <Card sx={{ ...commonCardSx, height:'400px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                <i className="icon-map-marker" style={{ color: '#0ea5e9', fontSize: '2rem' }} />
                            </Box>
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                }}
                            >
                                Pinpoint Location
                            </Typography>
                            <Typography variant="body1" sx={{fontSize: '14px', color: '#4b5563'}}>
                                Accurately record where each expense occurred. Search for establishments,
                                select from previously used event locations, or manually enter an address.
                                <br/><br/>
                                <Typography component="span" variant="body2"
                                            sx={{fontWeight: 'bold', color: '#0ea5e9'}}>Pro Tip:</Typography> Receipts
                                linked to Activities, Lodging, or Meals automatically populate
                                the location and cost!
                            </Typography>
                        </Card>
                    </Box>

                    {/* Add the LINK Box item */}
                    <Box sx={{...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <Card sx={{ ...commonCardSx, height:'400px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                <i className="icon-link" style={{ color: '#0ea5e9', fontSize: '2rem' }} />
                            </Box>
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                }}
                            >
                                Seamless Linking
                            </Typography>
                            <Typography variant="body1" sx={{fontSize: '14px', color: '#4b5563'}}>
                                Connect receipts directly to specific Activities, Meals, or Lodging entries.
                                This intelligent linking automatically imports relevant location and amount details,
                                streamlining your record-keeping.
                            </Typography>
                        </Card>
                    </Box>

                    {/* Add the AMOUNT Box item */}
                    <Box sx={{ ...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <Card sx={{ ...commonCardSx, height:'400px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                <i className="icon-wallet" style={{ color: '#0ea5e9', fontSize: '2rem' }} />
                            </Box>
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                }}
                            >
                                Track Every Amount
                            </Typography>
                            <Typography variant="body1" sx={{fontSize: '14px', color: '#4b5563'}}>
                                Accurately input the cost of your receipt and choose the correct currency
                                for precise financial tracking and comprehensive budget analysis.
                                <br/><br/>
                                <Typography component="span" variant="body2"
                                            sx={{fontWeight: 'bold', color: '#0ea5e9'}}>Pro Tip:</Typography> Ensure you
                                select the service date, and toggle the 'Business' option,
                                along with specifying the exact amount that qualifies, to correctly apply it towards
                                any Business Authorizations.
                            </Typography>
                        </Card>
                    </Box>

                    {/* Add the PHOTO Box item */}
                    <Box sx={{...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <Card sx={{ ...commonCardSx, height:'400px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                <i className="icon-picture2" style={{ color: '#0ea5e9', fontSize: '2rem' }} />
                            </Box>
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                }}
                            >
                                Visual Records
                            </Typography>
                            <Typography variant="body1" sx={{fontSize: '14px', color: '#4b5563'}}>
                                Snap and store! Effortlessly upload photos of your physical receipts to create a
                                secure, digital archive of all your expenditures, liberating you from paper clutter.
                                For business expenses, these images are automatically included in your comprehensive
                                expense reports, simplifying reconciliation.
                            </Typography>
                        </Card>
                    </Box>

                    {/* Add the NOTES Box item */}
                    <Box sx={{ ...responsiveBoxItemSx, maxWidth: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                        <Card sx={{ ...commonCardSx, height:'400px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                <i className="icon-pencil" style={{ color: '#0ea5e9', fontSize: '2rem' }} />
                            </Box>
                            <Typography
                                variant="h5"
                                component="h3"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem'
                                }}
                            >
                                Add Detailed Notes
                            </Typography>
                            <Typography variant="body1" sx={{fontSize: '14px', color: '#4b5563'}}>
                                Include any specific instructions, important details, or contextual
                                information for each receipt. This helps you remember key aspects of
                                your spending for future reference.
                            </Typography>
                        </Card>
                    </Box>
                </Box>


                <Typography variant="h4" component="h2" sx={{
                    fontSize: '18px',
                    fontWeight: 700, // font-bold
                    color: '#1f2937', // text-gray-800
                    marginBottom: '1.5rem', // mb-6
                    textAlign: 'center',
                }}>
                    Editing Receipt Details: Adjusting Your Records! ✏️
                </Typography>
                <Typography variant="h6" sx={{
                    fontSize: '14px',
                    color: '#4b5563', // text-gray-600
                    marginBottom: '2rem', // mb-8
                    textAlign: 'center',
                }}>
                    Need to make a change? Easily update any receipt entry:
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '24px', // Equivalent to spacing={3}
                        marginBottom: '3rem',
                        justifyContent: 'center' // Center items when they wrap
                    }}
                >
                    {/* Step 1: Find Your Receipt */}
                    <Box sx={responsiveBoxItemWithStepSx}>
                        <StepNumberCircle step="1" color="#0ea5e9" />
                        <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                <i className="icon-search" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                            </Box>
                            <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                Find Your Receipt
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                Quickly locate the specific receipt you wish to modify within your receipt list.
                            </Typography>
                        </Card>
                    </Box>
                    {/* Step 2: Open for Editing */}
                    <Box sx={responsiveBoxItemWithStepSx}>
                        <StepNumberCircle step="2" color="#0ea5e9" />
                        <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                <i className="icon-pencil" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                            </Box>
                            <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                Open for Editing
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                Click the icon on the left side of the receipt's entry to access its detailed options.
                            </Typography>
                        </Card>
                    </Box>
                    {/* Step 3: Make Your Changes */}
                    <Box sx={responsiveBoxItemWithStepSx}>
                        <StepNumberCircle step="3" color="#0ea5e9" />
                        <Card sx={{ ...stepCardBaseSx, height:'250px', borderColor: '#0ea5e9', overflow:'auto' }}>
                            <Box sx={{...commonIconContainerSx, backgroundColor: '#e0f2fe'}}>
                                <i className="icon-check" style={{ color: '#0ea5e9', fontSize: '2rem' }}/>
                            </Box>
                            <Typography variant="h6" component="h4" sx={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '0.25rem' }}>
                                Make Your Changes
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '14px', color: '#4b5563' }}>
                                The receipt's details will appear for modification.
                                Adjust the location, notes, photo, amount, date, business expense status,
                                linked item, or privacy setting as needed.
                            </Typography>
                        </Card>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default InfoReceipts;
